// stateUtils.js - Robust Multi-Pass Logic Engine

export const calculateScore = (moves = 0, targetMoves = 1) => {
  const baseScore = 100;
  const movePenalty = Math.max(0, (moves - targetMoves) * 10);
  return Math.max(10, baseScore - movePenalty);
};

export const initializeState = (nodes = [], edges = []) => {
  const initialSwitchStates = {};
  const initialActiveEdges = {};

  nodes.forEach((node) => {
    if (node.type === "input") {
      initialSwitchStates[node.id] =
        node.data?.defaultState !== undefined
          ? !!node.data.defaultState
          : !!node.data?.isOn;
    }
  });

  edges.forEach((edge) => {
    initialActiveEdges[edge.id] = false;
  });

  return { initialSwitchStates, initialActiveEdges };
};

export const calculateIntermediateStates = (nodes = [], edges = [], switchStates = {}, activeEdges = {}) => {
  const intermediateStates = {};

  // Perform multiple passes to propagate signal across multi-tier circuit layers
  const maxPasses = Math.max(1, nodes.length + 1);

  for (let pass = 0; pass < maxPasses; pass++) {
    edges.forEach((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const targetNode = nodes.find((node) => node.id === edge.target);

      if (sourceNode && targetNode) {
        // Determine the boolean state of the source node
        const sourceState =
          sourceNode.type === "input"
            ? !!switchStates[sourceNode.id]
            : !!intermediateStates[sourceNode.id];

        // Mark edge active state
        activeEdges[edge.id] = sourceState;

        // Calculate gate logic for target node
        if (targetNode.type === "gate") {
          const gateInputs = edges
            .filter((e) => e.target === targetNode.id)
            .map((e) => {
              const srcN = nodes.find((n) => n.id === e.source);
              return srcN?.type === "input"
                ? !!switchStates[e.source]
                : !!intermediateStates[e.source];
            });

          const label = (targetNode.data?.label || "OR").toUpperCase().trim();
          const gateLogic = {
            AND: () => gateInputs.length > 0 && gateInputs.every(Boolean),
            OR: () => gateInputs.length > 0 && gateInputs.some(Boolean),
            NOT: () => (gateInputs.length > 0 ? !gateInputs[0] : false),
            NAND: () => gateInputs.length > 0 && !gateInputs.every(Boolean),
            NOR: () => gateInputs.length > 0 && !gateInputs.some(Boolean),
            XOR: () => gateInputs.length > 0 && gateInputs.filter(Boolean).length % 2 === 1,
            XNOR: () => gateInputs.length > 0 && gateInputs.filter(Boolean).length % 2 === 0,
            BUFFER: () => (gateInputs.length > 0 ? Boolean(gateInputs[0]) : false),
          };

          intermediateStates[targetNode.id] = gateLogic[label] ? gateLogic[label]() : false;
        } else if (targetNode.type === "output") {
          intermediateStates[targetNode.id] = sourceState;
        }
      }
    });
  }

  return intermediateStates;
};

export const completionLogicFC = (switchStates = {}, activeEdges = {}, edges = [], outputNodeId = "") => {
  if (!outputNodeId) return false;

  // Find all edges pointing to the target completion light
  const outputEdges = edges.filter((edge) => edge.target === outputNodeId);

  // Level is complete if any edge pointing to output node is active (true)
  return outputEdges.length > 0 && outputEdges.some((edge) => !!activeEdges[edge.id]);
};
